import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Database,
  Plus,
  Settings,
  Trash2,
  LogOut,
  User,
  Loader2,
  Eye,
  Link as LinkIcon,
  Folder,
  FileText,
  ExternalLink,
  Key,
  HelpCircle
} from "lucide-react";
import { toast } from "sonner";
import { ThemeToggle } from "@/components/ThemeToggle";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout, token, csrfToken } = useAuth();
  const [portals, setPortals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [schema, setSchema] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [hasAirtableCredentials, setHasAirtableCredentials] = useState(null);
  const [showAirtableSetup, setShowAirtableSetup] = useState(false);
  const [airtableBases, setAirtableBases] = useState([]);
  const [loadingBases, setLoadingBases] = useState(false);
  const [newPortal, setNewPortal] = useState({
    name: "",
    description: "",
    airtable_api_key: "",
    airtable_base_id: ""
  });

  const authHeaders = {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };

  // Headers including CSRF token for state-changing requests
  const authHeadersWithCsrf = {
    headers: { 
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {})
    }
  };

  useEffect(() => {
    fetchPortals();
    fetchSchema();
  }, []);

  // Check Airtable credentials and fetch bases when token is available
  useEffect(() => {
    if (token) {
      checkAirtableCredentials();
    }
  }, [token]);

  const checkAirtableCredentials = async () => {
    try {
      const response = await axios.get(`${API}/user/airtable-credentials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('[checkAirtableCredentials] Response:', response.data);
      setHasAirtableCredentials(response.data.has_credentials);
      
      // If user has credentials, fetch available bases
      if (response.data.has_credentials) {
        fetchAirtableBases();
      }
    } catch (error) {
      console.error("Failed to check Airtable credentials:", error);
      setHasAirtableCredentials(false);
    }
  };

  const fetchAirtableBases = async () => {
    setLoadingBases(true);
    try {
      const response = await axios.get(`${API}/user/airtable-bases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAirtableBases(response.data.bases || []);
    } catch (error) {
      console.error("Failed to fetch Airtable bases:", error);
      toast.error("Failed to fetch Airtable bases");
    } finally {
      setLoadingBases(false);
    }
  };

  const fetchPortals = async () => {
    try {
      const response = await axios.get(`${API}/portals`, authHeaders);
      setPortals(response.data.portals || []);
    } catch (error) {
      toast.error("Failed to load portals");
    } finally {
      setLoading(false);
    }
  };

  const fetchSchema = async () => {
    try {
      const response = await axios.get(`${API}/airtable/schema`, authHeaders);
      setSchema(response.data);
    } catch (error) {
      console.error("Failed to fetch schema:", error);
    }
  };

  const handleSaveAirtableCredentials = async () => {
    if (!newPortal.airtable_api_key || !newPortal.airtable_base_id) {
      toast.error("Please enter both API key and Base ID");
      return;
    }
    
    setCreating(true);
    try {
      await axios.put(`${API}/user/airtable-credentials`, {
        airtable_api_key: newPortal.airtable_api_key,
        airtable_base_id: newPortal.airtable_base_id
      }, authHeadersWithCsrf);
      
      setHasAirtableCredentials(true);
      setShowAirtableSetup(false);
      toast.success("Airtable credentials saved successfully!");
      // Refresh schema with new credentials
      fetchSchema();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save Airtable credentials");
    } finally {
      setCreating(false);
    }
  };

  const handleCreatePortal = async (e) => {
    e.preventDefault();
    if (!newPortal.name) {
      toast.error("Please enter a portal name");
      return;
    }
    
    // Require base selection if user has credentials
    if (hasAirtableCredentials && !newPortal.airtable_base_id) {
      toast.error("Please select an Airtable base");
      return;
    }
    
    // If user doesn't have stored credentials, require them in the form
    if (!hasAirtableCredentials) {
      if (!newPortal.airtable_api_key) {
        toast.error("Please enter your Airtable API key");
        return;
      }
      if (!newPortal.airtable_base_id) {
        toast.error("Please enter your Airtable Base ID");
        return;
      }
    }

    setCreating(true);
    try {
      // Send portal name, description, and selected base ID
      const portalData = {
        name: newPortal.name,
        description: newPortal.description,
        airtable_base_id: newPortal.airtable_base_id || null,
        // Only include API key if user is setting up new credentials
        ...(hasAirtableCredentials ? {} : { airtable_api_key: newPortal.airtable_api_key })
      };
      
      const response = await axios.post(`${API}/portals`, portalData, authHeadersWithCsrf);
      setPortals([...portals, response.data]);
      setCreateDialogOpen(false);
      setNewPortal({ name: "", description: "", airtable_api_key: "", airtable_base_id: "" });
      toast.success("Portal created! Now add pages to it.");
      navigate(`/portal/${response.data.portal_id}/config`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create portal");
    } finally {
      setCreating(false);
    }
  };

  const handleDeletePortal = async (portalId) => {
    if (!window.confirm("Are you sure you want to delete this portal and all its pages?")) return;

    try {
      await axios.delete(`${API}/portals/${portalId}`, authHeadersWithCsrf);
      setPortals(portals.filter((p) => p.portal_id !== portalId));
      toast.success("Portal deleted");
    } catch (error) {
      toast.error("Failed to delete portal");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const copyShareLink = (portal) => {
    const url = `${window.location.origin}/p/${portal.share_token}`;
    navigator.clipboard.writeText(url);
    toast.success("Share link copied!");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Database className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold text-foreground tracking-tight">Paeora</span>
          </Link>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 text-muted-foreground">
              <User className="w-4 h-4" />
              <span className="text-sm hidden sm:inline">{user?.email}</span>
            </div>
            <ThemeToggle />
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground hover:bg-muted"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Airtable Onboarding Modal - Shows automatically for new users */}
        <Dialog open={hasAirtableCredentials === false && !loading} onOpenChange={() => {}}>
          <DialogContent className="bg-card border-border text-foreground sm:max-w-lg" onPointerDownOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Database className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <DialogTitle className="text-xl">Connect Your Airtable</DialogTitle>
                  <DialogDescription className="text-muted-foreground">
                    One-time setup to link your Airtable account
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {/* Step 1: API Token */}
              <div className="p-4 bg-secondary/50 rounded-lg border border-border">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-sm font-bold shrink-0">1</div>
                  <div className="flex-1">
                    <Label className="text-foreground font-medium">Personal Access Token</Label>
                    <p className="text-xs text-muted-foreground mt-1 mb-3">
                      Create a token at Airtable with access to your bases
                    </p>
                    <a 
                      href="https://airtable.com/create/tokens" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-flex items-center gap-1 text-sm text-primary hover:underline mb-3"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Open Airtable Token Page →
                    </a>
                    <Input
                      className="bg-background border-border text-foreground focus:border-primary font-mono text-sm"
                      placeholder="pat_xxxxxxxxxxxx..."
                      type="password"
                      value={newPortal.airtable_api_key}
                      onChange={(e) => setNewPortal({ ...newPortal, airtable_api_key: e.target.value })}
                      data-testid="onboarding-api-key-input"
                    />
                    <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-600 dark:text-amber-400">
                      <strong>Tip:</strong> When creating your token, select these scopes:
                      <ul className="list-disc list-inside mt-1 space-y-0.5">
                        <li>data.records:read</li>
                        <li>data.records:write</li>
                        <li>schema.bases:read</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2: Base ID */}
              <div className="p-4 bg-secondary/50 rounded-lg border border-border">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-sm font-bold shrink-0">2</div>
                  <div className="flex-1">
                    <Label className="text-foreground font-medium">Base ID</Label>
                    <p className="text-xs text-muted-foreground mt-1 mb-2">
                      Find this from the Airtable API documentation
                    </p>
                    <a 
                      href="https://airtable.com/api" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-flex items-center gap-1 text-sm text-primary hover:underline mb-3"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Open Airtable API Page →
                    </a>
                    <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-600 dark:text-amber-400">
                      <strong>How to find your Base ID:</strong>
                      <ol className="list-decimal list-inside mt-1 space-y-0.5">
                        <li>Go to <span className="font-mono">airtable.com/api</span></li>
                        <li>Select the correct base</li>
                        <li>Go to Authentication section</li>
                        <li>Copy the URL that begins with:<br/>
                          <span className="font-mono text-xs">https://api.airtable.com/v0/<span className="text-primary font-bold">appXXXXXXXXXXX</span></span>
                        </li>
                        <li>Paste the full URL below - we'll extract the Base ID</li>
                      </ol>
                    </div>
                    <Input
                      className="mt-3 bg-background border-border text-foreground focus:border-primary font-mono text-sm"
                      placeholder="https://api.airtable.com/v0/appXXXXXXX or appXXXXXXX"
                      value={newPortal.airtable_base_id}
                      onChange={(e) => {
                        // Extract base ID if user pastes full URL
                        let value = e.target.value;
                        const match = value.match(/app[a-zA-Z0-9]+/);
                        if (match) {
                          value = match[0];
                        }
                        setNewPortal({ ...newPortal, airtable_base_id: value });
                      }}
                      data-testid="onboarding-base-id-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button
                className="w-full btn-primary"
                onClick={handleSaveAirtableCredentials}
                disabled={creating || !newPortal.airtable_api_key || !newPortal.airtable_base_id}
                data-testid="save-airtable-credentials-btn"
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Database className="w-4 h-4 mr-2" />
                )}
                Connect Airtable
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground tracking-tight">Your Portals</h1>
            <p className="text-muted-foreground mt-1">Each portal contains multiple pages for your clients</p>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-portal-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Portal
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border text-foreground">
              <DialogHeader>
                <DialogTitle>Create New Portal</DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  A portal is a container for multiple pages. You'll add pages after creating the portal.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreatePortal} className="space-y-4 mt-4">
                <div>
                  <Label className="text-foreground/80">Portal Name *</Label>
                  <Input
                    className="mt-2 bg-background border-border text-foreground focus:border-primary"
                    placeholder="Client Portal"
                    value={newPortal.name}
                    onChange={(e) => setNewPortal({ ...newPortal, name: e.target.value })}
                    required
                    data-testid="portal-name-input"
                  />
                </div>
                <div>
                  <Label className="text-foreground/80">Description</Label>
                  <Input
                    className="mt-2 bg-background border-border text-foreground focus:border-primary"
                    placeholder="Portal for client data access"
                    value={newPortal.description}
                    onChange={(e) => setNewPortal({ ...newPortal, description: e.target.value })}
                    data-testid="portal-description-input"
                  />
                </div>
                
                {/* Airtable Connection Section - Only show if user doesn't have stored credentials */}
                {hasAirtableCredentials === false && (
                  <div className="pt-4 border-t border-border">
                    <h4 className="text-sm font-semibold text-foreground mb-3">Airtable Connection</h4>
                    <p className="text-xs text-muted-foreground mb-3">
                      Connect your Airtable account. You only need to do this once.
                    </p>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-foreground/80">Airtable API Key (Personal Access Token) *</Label>
                        <Input
                          className="mt-2 bg-background border-border text-foreground focus:border-primary font-mono text-sm"
                          placeholder="pat..."
                          type="password"
                          value={newPortal.airtable_api_key}
                          onChange={(e) => setNewPortal({ ...newPortal, airtable_api_key: e.target.value })}
                          required
                          data-testid="airtable-api-key-input"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Get your token from <a href="https://airtable.com/create/tokens" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">airtable.com/create/tokens</a>
                        </p>
                      </div>
                      <div>
                        <Label className="text-foreground/80">Airtable Base ID *</Label>
                        <Input
                          className="mt-2 bg-background border-border text-foreground focus:border-primary font-mono text-sm"
                          placeholder="app..."
                          value={newPortal.airtable_base_id}
                          onChange={(e) => setNewPortal({ ...newPortal, airtable_base_id: e.target.value })}
                          required
                          data-testid="airtable-base-id-input"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Find your Base ID in the URL when viewing your base: airtable.com/<span className="text-primary">appXXXXXX</span>/...
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Base selector - show when user has credentials */}
                {hasAirtableCredentials === true && (
                  <div className="pt-4 border-t border-border">
                    <div className="flex items-center gap-2 text-sm text-emerald-500 mb-3">
                      <Database className="w-4 h-4" />
                      <span>Using your saved Airtable connection</span>
                    </div>
                    
                    <div>
                      <Label className="text-foreground/80">Select Airtable Base *</Label>
                      {loadingBases ? (
                        <div className="mt-2 p-3 bg-muted/30 rounded text-sm text-muted-foreground">
                          Loading bases...
                        </div>
                      ) : airtableBases.length > 0 ? (
                        <select
                          className="mt-2 w-full p-2 bg-background border border-border rounded text-foreground focus:border-primary"
                          value={newPortal.airtable_base_id}
                          onChange={(e) => setNewPortal({ ...newPortal, airtable_base_id: e.target.value })}
                          required
                          data-testid="airtable-base-select"
                        >
                          <option value="">Select a base...</option>
                          {airtableBases.map((base) => (
                            <option key={base.id} value={base.id}>
                              {base.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="mt-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded text-sm text-yellow-500">
                          No bases found. Make sure your API key has access to at least one base.
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                <DialogFooter className="mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    className="btn-secondary"
                    onClick={() => setCreateDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-primary" disabled={creating} data-testid="create-portal-submit">
                    {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Portal"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Portals Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-card border border-border rounded-lg p-6 h-48 skeleton" />
            ))}
          </div>
        ) : portals.length === 0 ? (
          <div className="text-center py-24">
            <div className="w-16 h-16 bg-card border border-border rounded-lg flex items-center justify-center mx-auto mb-6">
              <Folder className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-2">No portals yet</h3>
            <p className="text-muted-foreground mb-6">Create your first portal to start organizing pages</p>
            <Button className="btn-primary" onClick={() => setCreateDialogOpen(true)} data-testid="empty-create-portal-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Portal
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {portals.map((portal) => (
              <div
                key={portal.portal_id}
                className="bg-card border border-border rounded-lg p-6 card-hover group"
                data-testid={`portal-card-${portal.portal_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center group-hover:bg-accent transition-colors duration-300">
                    <Folder className="w-5 h-5 text-primary group-hover:text-accent-foreground transition-colors duration-300" />
                  </div>
                  <div className="flex items-center gap-1">
                    {portal.is_public && (
                      <span className="text-xs bg-accent/20 text-accent px-2 py-1 rounded">
                        Public
                      </span>
                    )}
                  </div>
                </div>
                <h3 className="text-lg font-bold text-foreground mb-1 truncate">{portal.name}</h3>
                <p className="text-sm text-muted-foreground mb-2 truncate">
                  {portal.description || "No description"}
                </p>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                  <FileText className="w-4 h-4" />
                  <span>{portal.page_count || 0} pages</span>
                </div>
                <div className="flex items-center gap-2 pt-4 border-t border-border/50">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-foreground hover:bg-muted flex-1"
                    onClick={() => navigate(`/portal/${portal.portal_id}`)}
                    data-testid={`view-portal-${portal.portal_id}`}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-foreground hover:bg-muted flex-1"
                    onClick={() => navigate(`/portal/${portal.portal_id}/config`)}
                    data-testid={`config-portal-${portal.portal_id}`}
                  >
                    <Settings className="w-4 h-4 mr-1" />
                    Config
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-primary hover:bg-muted"
                    onClick={() => copyShareLink(portal)}
                    data-testid={`share-portal-${portal.portal_id}`}
                  >
                    <LinkIcon className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    onClick={() => handleDeletePortal(portal.portal_id)}
                    data-testid={`delete-portal-${portal.portal_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Airtable Connection Status */}
        <div className="mt-12 bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
                <Database className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-bold text-foreground">Airtable Connection</h3>
                <p className="text-sm text-muted-foreground">
                  {schema ? `${schema.tables?.length || 0} tables available` : "Loading..."}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${schema ? 'bg-accent' : 'bg-yellow-500'}`} />
              <span className="text-sm text-muted-foreground">
                {schema ? "Connected" : "Connecting..."}
              </span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
